import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { DeviceAuthorizationSettings, useRepositoryMutation } from '@mews-commander/data-access';
import {
    createExperimentalZodFormResolver,
    lt,
    showErrorAlert,
    showSuccessAlert,
    useFormControls,
} from '@mews/framework';
import { useOwnerIdQueryPayload } from '../../queries/useOwnerId';

export enum ConfigureDeviceAuthFieldNames {
    Enabled = 'enabled',
}

export interface ConfigureDeviceAuthFormData {
    enabled: boolean;
}

export const deviceAuthValidationSchema = z.object({
    [ConfigureDeviceAuthFieldNames.Enabled]: z.boolean(),
});

export const initialDeviceAuthValues = {
    [ConfigureDeviceAuthFieldNames.Enabled]: false,
};

interface UseConfigureDeviceAuthFormProps {
    id: string;
    data?: DeviceAuthorizationSettings;
    onSuccess: () => void;
}

export function useConfigureDeviceAuthForm({ id, data, onSuccess }: UseConfigureDeviceAuthFormProps) {
    const ownerIdPayload = useOwnerIdQueryPayload();
    const defaultValues = useMemo(() => parseDeviceAuthConfigToForm(data), [data]);

    const { mutateAsync: updateDeviceAuth } = useRepositoryMutation('deviceAuthorization', 'updateSettings', {
        invalidateQueries: {
            deviceAuthorization: ['getSettings' as const],
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    const {
        validate,
        errors,
        reset: internalReset,
        ...otherMethods
    } = useFormControls<ConfigureDeviceAuthFormData>({
        id,
        onSubmit: async (values: ConfigureDeviceAuthFormData, { reset }) => {
            try {
                await updateDeviceAuth({
                    ...ownerIdPayload,
                    enforceDeviceAuthorization: {
                        value: values.enabled,
                    },
                });
                showSuccessAlert({
                    description: lt(
                        values.enabled
                            ? 'DeviceAuthConfigurationStatusChangeActivate'
                            : 'DeviceAuthConfigurationStatusChangeDeactivate'
                    ),
                });
                onSuccess();
                reset();
            } catch (error) {
                showErrorAlert({ description: <>{error.message}</> });
            }
        },
        mode: 'onBlur',
        defaultValues,
        resolver: createExperimentalZodFormResolver(deviceAuthValidationSchema),
    });

    // Since the form is not being mounted/unmounted, we need to reset the form when the default values change
    useEffect(() => {
        internalReset(defaultValues);
    }, [internalReset, defaultValues]);

    return {
        validate,
        reset: () => internalReset(defaultValues),
        ...otherMethods,
    };
}

export function parseDeviceAuthConfigToForm(data?: DeviceAuthorizationSettings): ConfigureDeviceAuthFormData {
    return {
        enabled: !!data?.deviceAuthorizationEnforced,
    };
}
